import {
    SavedNote,
    SendNotesViewProps,
} from './types'
import {
    SubmitHandler,
    useForm,
} from 'react-hook-form'
import {
    styledFieldset,
    styledInput,
    styledLabel,
} from '../Form/styles/FormView'
import Button from '../Button'
import Checkbox from '../Checkbox'
import {NinchatQueueNote} from '../../api/types/properties'
import React from 'react'
import {styled} from '../../stitches/config'
import styles from './styles/SendNotesView.module.scss'
import {useTranslation} from 'react-i18next'

type FormValues = Record<string, unknown>
type NotesState = {
    error: string | null
}

const SendNotesView = (props: SendNotesViewProps): React.ReactElement => {
    const {
        notes,
        onSave,
        savedNotes,
    } = props

    const {t} = useTranslation()
    const defaultState: NotesState = {error: null}

    const [state, setState] = React.useState(defaultState)

    const {
        formState: {errors},
        handleSubmit,
        register,
        setValue,
    } = useForm<FormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    })

    const StyledFieldset = styled('fieldset', styledFieldset)
    const StyledInput = styled('input', styledInput)
    const StyledLabel = styled('label', styledLabel)
    const StyledTextarea = styled('textarea', styledInput)

    const getItem = (item: NinchatQueueNote): React.ReactElement => {
        switch (item.element) {
            case 'textarea':
                return (
                    <div key={item.name}>
                        <StyledLabel htmlFor={item.name}>{item.label}</StyledLabel>
                        <StyledTextarea
                            {...register(item.name, {
                                pattern: new RegExp(item.pattern, 'u'),
                                required: item.required,
                            })}
                            aria-invalid={errors[item.name] ? 'true' : 'false'}
                            defaultValue={item.value}
                            id={item.name}
                        />
                    </div>
                )
            case 'input':
            default:
                if (item.type === 'checkbox') {
                    return (
                        <Checkbox
                            {...register(item.name, {required: item.required})}
                            aria-invalid={errors[item.name] ? 'true' : 'false'}
                            defaultChecked={Boolean(item.value)}
                            id={item.name}
                            key={item.name}
                            label={item.label}
                        />
                    )
                }

                return (
                    <div key={item.name}>
                        <StyledLabel htmlFor={item.name}>{item.label}</StyledLabel>
                        <StyledInput
                            {...register(item.name, {
                                pattern: new RegExp(item.pattern, 'u'),
                                required: item.required,
                            })}
                            aria-invalid={errors[item.name] ? 'true' : 'false'}
                            defaultValue={item.value}
                            id={item.name}
                            type={item.type}
                        />
                    </div>
                )
        }
    }

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        // A little trick to convert empty strings to null values
        let finalData = JSON.parse(JSON.stringify(data, (key, value) => (key && value === '' ? null : value)))

        // Remove null and false values
        finalData = Object.fromEntries(Object.entries(finalData).filter(([_, v]) => v !== null && v !== false))
        onSave(finalData)
    }

    React.useEffect(() => {
        let notFound = false

        savedNotes.forEach((_note: SavedNote) => {
            const index = notes.findIndex((n) => n.name === _note.name)
            if (index > -1) {
                setValue(notes[index].name, _note.value)
            } else {
                notFound = true
            }
        })

        if (notFound) {
            setState({
                // eslint-disable-next-line max-len
                error: t('Saved notes contains a value that no longer exists. This is probably due notes settings being changed after notes were saved. Saving notes would lead to a loss of data, hence the form is disabled.'),
            })
        } else {
            setState({error: null})
        }
    }, [notes])

    return (
        <form className={styles.SendNotesView} onSubmit={handleSubmit(onSubmit)}>
            {!!state.error &&
                <div className={styles.errorMessageContainer}>
                    <div><span>{state.error}</span></div>
                </div>}
            <StyledFieldset>
                {
                    notes.map((item: NinchatQueueNote) => getItem(item))
                }
            </StyledFieldset>
            <div className={`rowParent ${styles.footer}`}>
                <Button color='filled' disabled={Boolean(state.error)} tabIndex={-1} text={t('Save')} type='submit' />
            </div>
        </form>
    )
}

export default SendNotesView
